import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute, Link } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "@/components/site/Page";
import { pathFor } from "@/lib/i18n";

const TITLE = "Relocation Consultation – Swedish International School";
const DESCRIPTION =
  "Included if you wish, for families enrolling a child full-time for at least a term: a 45-minute session on the start of school and moving to the Costa del Sol.";

export const Route = createFileRoute("/en/relocation-consultation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/en/relocation-consultation") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "en_GB" },
      ...ogImage(),
    ],
    links: seoLinks("/en/relocation-consultation"),
  }),
  component: RelocationPage,
});

function RelocationPage() {
  return (
    <>
      <PageHero
        eyebrow="Included in your application"
        title="A 45-minute consultation before the move"
        intro="The consultation is included if you'd like it, and applies to families enrolling at least one child full-time for at least one term. Once you've been accepted and the enrolment fee has been paid, we invite the family to a personal session of about 45 minutes where we go through the start of school and the practicalities of life on the Costa del Sol."
      />

      <Prose>
        <p>
          Moving abroad with children involves many decisions in a short space of time. We have
          welcomed families from across the Nordics for many years and see the same questions come
          up again and again. That's why we offer every new family a consultation where you can ask
          your questions to someone who knows both the school and the area.
        </p>

        <h2>What we cover</h2>
        <p>
          We go through the practical things that are worth thinking about before the move and
          what awaits once you've arrived, and we answer your questions on everything from the
          start of school to everyday life. What the consultation covers always depends on your
          situation – no two families are the same.
        </p>

        <h2>How it works</h2>
        <ul>
          <li>Included if you wish, for families with at least one child full-time for at least one term – not included with a short stay.</li>
          <li>Tick the box in the application form to request the consultation, and we'll be in touch.</li>
          <li>Takes place at the earliest 15 days after the contract is signed and payment is completed.</li>
          <li>About 45 minutes, in person at the school or online.</li>
          <li>Once per family – regardless of how many children you enrol.</li>
        </ul>

        <h2>Good to know</h2>
        <p>
          The consultation is general guidance from the school based on our experience of welcoming
          families who have relocated. It does not replace legal, tax or financial advice, and we
          do not arrange housing. If you need specialist support, we can point you in the right
          direction.
        </p>

        <p>
          <Link className="text-primary link-quiet" to={pathFor("/ansokan", "en") as never}>
            Submit an application
          </Link>{" "}
          or{" "}
          <Link className="text-primary link-quiet" to={pathFor("/avgifter", "en") as never}>
            read more about fees
          </Link>
          .
        </p>
      </Prose>

      <div className="pb-16 sm:pb-24">
        <CtaBand
          title="Would you like the consultation?"
          text="Tick the box in the application form and we'll book a time once you've been accepted. Applies to families with at least one child full-time, for at least one term."
          locale="en"
        />
      </div>
    </>
  );
}
